import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useShiftFreeze } from "@/context/ShiftFreezeContext";
import { Button } from "@/components/ui/button";
import { Coffee, Play, Clock, Timer, MoonStar, CheckCircle2, LogOut } from "lucide-react";

// Admin spec 2026-02-07:
//   • Idle grace = 5 min. This intentionally forgives a quick tab-switch
//     to Gmail/Slack/Docs. Agents whose *only* activity happens outside
//     our tab still get counted as idle after 5 min — that's the trade
//     browsers force on us (we can't see events on other tabs).
//   • Extra-break threshold = 10 min. When an agent returns after being
//     away >= 10 min we prompt them for a reason and log an extra-break
//     entry (via /shifts/extra-break) so admin can audit unplanned absences.
const IDLE_THRESHOLD_MS = 5 * 60 * 1000;       // 5 min → idle
const EXTRA_BREAK_THRESHOLD_MS = 10 * 60 * 1000; // >10 min → prompt for reason
const HEARTBEAT_INTERVAL_MS = 60 * 1000 + Math.floor(Math.random() * 10_000); // per-tab jitter
// Reasons admins can break down break-time usage by, per user agreement.
const BREAK_REASONS = ["Lunch", "Tea/Coffee", "Meeting", "Personal", "Restroom", "Other"];
const EXTRA_BREAK_REASONS = ["Bio break", "Water/Snack", "Emergency", "Phone call", "Distraction", "Other"];

function fmt(secs) {
  secs = Math.max(0, Math.floor(secs));
  const h = String(Math.floor(secs / 3600)).padStart(2, "0");
  const m = String(Math.floor((secs % 3600) / 60)).padStart(2, "0");
  const s = String(secs % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
}

export default function ShiftCounter({ compact = false }) {
  const { status: freezeStatus } = useShiftFreeze();
  const [shift, setShift] = useState(null);
  const [tick, setTick] = useState(0);
  const [onBreak, setOnBreak] = useState(false);
  const [breakReason, setBreakReason] = useState(null);
  const [reasonOpen, setReasonOpen] = useState(false);

  // Refs are the source of truth for accumulated seconds so we can keep state
  // stable across re-renders without losing time when the component unmounts.
  const idleSecondsRef = useRef(0);
  const breakSecondsRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const lastTickRef = useRef(Date.now());
  const onBreakRef = useRef(false);
  // True while the CURRENT break was auto-started by a scheduled break
  // window (freeze). We only auto-END breaks we auto-started — a manual
  // "Take Break" is always ended by the agent.
  const autoBreakRef = useRef(false);
  // Dedupe guards for the freeze-driven shift lifecycle effect.
  const attachTriedRef = useRef(false);
  const endingRef = useRef(false);
  // Away-timer used ONLY for the extra-break prompt. Independent from
  // idleSecondsRef because we want to prompt exactly once per "away
  // window" — not on every tick past 10 min.
  const awayStartRef = useRef(null);          // Date.now() when we first went silent
  const extraBreakPromptShown = useRef(false); // dedupe prompt per away-window
  // Extra-break modal state (surfaced on the return-from-absence event).
  const [extraBreakPrompt, setExtraBreakPrompt] = useState(null); // { minutes: 12 } | null

  // 1) Shift lifecycle, driven by the freeze status (admin spec 2026-07:
  //    the report must follow the SCHEDULED shift + break windows).
  //    • Not frozen + no shift → start (or re-attach to) the shift.
  //    • Frozen pre-shift/weekend → only re-attach if a shift is already
  //      open (page refresh mid-break); never CREATE one before the shift.
  //    • Frozen for shift end → auto-END the shift so idle time stops
  //      accumulating past the scheduled end.
  const applyShiftDoc = useCallback((data) => {
    setShift(data);
    idleSecondsRef.current = data?.idle_seconds || 0;
    breakSecondsRef.current = data?.break_seconds || 0;
    onBreakRef.current = !!data?.on_break;
    setOnBreak(!!data?.on_break);
    setBreakReason(data?.current_break_reason || null);
  }, []);

  useEffect(() => {
    if (!freezeStatus) return; // wait for the first status (fail-open on error)
    const frozen = !!freezeStatus.frozen;
    const reason = freezeStatus.reason || "";
    (async () => {
      // Auto-end at scheduled shift end — the overlay blocks all work, so
      // keeping the clock running only inflates idle on the report. Works
      // even when the local shift never attached (e.g. page opened after
      // the scheduled end while a server-side shift is still open).
      if (frozen && (reason === "shift_ended" || reason === "after_shift") && !endingRef.current) {
        endingRef.current = true;
        try {
          if (shift?.id) {
            await api.post("/shifts/heartbeat", {
              idle_seconds: Math.round(idleSecondsRef.current),
              break_seconds: Math.round(breakSecondsRef.current),
              on_break: false,
            });
            await api.post("/shifts/end");
          } else {
            const { data: mine } = await api.get("/shifts/my");
            if (mine?.id) await api.post("/shifts/end");
          }
        } catch (e) {
          console.debug("[ShiftCounter] auto shift-end failed:", e?.response?.status || e?.message);
        }
        setShift(null);
        idleSecondsRef.current = 0;
        breakSecondsRef.current = 0;
        onBreakRef.current = false;
        autoBreakRef.current = false;
        setOnBreak(false);
        setBreakReason(null);
        endingRef.current = false;
        return;
      }
      if (shift?.id) return;
      if (!frozen) {
        attachTriedRef.current = false;
        try {
          const { data } = await api.post("/shifts/start");
          applyShiftDoc(data);
        } catch (e) {
          console.warn("shift/start failed", e?.message);
        }
      } else if (reason === "break_window" || reason === "voluntary_break") {
        // Refresh mid-break: re-attach so counters keep the right shift.
        if (attachTriedRef.current) return;
        attachTriedRef.current = true;
        try {
          const { data: mine } = await api.get("/shifts/my");
          if (mine?.id) {
            const { data } = await api.post("/shifts/start"); // credits offline gap correctly
            applyShiftDoc(data);
          }
        } catch (e) {
          console.debug("[ShiftCounter] frozen attach skipped:", e?.response?.status || e?.message);
        }
      }
      // before_shift / weekend with no open shift: do nothing — the shift
      // starts automatically when the freeze lifts at the scheduled time.
    })();
  }, [freezeStatus, shift?.id, applyShiftDoc]);

  // 1b) Scheduled-break attribution — when the freeze overlay locks the app
  //     for a fixed break window, count that time as BREAK (not idle) and
  //     log a break session with the scheduled label so the report's Break
  //     column and Break Reasons reflect the schedule.
  useEffect(() => {
    if (!freezeStatus || !shift?.id) return;
    const inScheduledBreak = !!freezeStatus.frozen && freezeStatus.reason === "break_window";
    if (inScheduledBreak && !onBreakRef.current) {
      const label = `${freezeStatus.break_window?.label || "Break"} (scheduled)`;
      autoBreakRef.current = true;
      onBreakRef.current = true;
      setOnBreak(true);
      setBreakReason(label);
      (async () => {
        try {
          await api.post("/shifts/break", { action: "start", reason: label });
          await api.post("/shifts/heartbeat", {
            idle_seconds: Math.round(idleSecondsRef.current),
            break_seconds: Math.round(breakSecondsRef.current),
            on_break: true,
          });
        } catch (e) {
          console.debug("[ShiftCounter] scheduled-break start failed:", e?.response?.status || e?.message);
        }
      })();
    } else if (!inScheduledBreak && autoBreakRef.current) {
      autoBreakRef.current = false;
      onBreakRef.current = false;
      setOnBreak(false);
      setBreakReason(null);
      lastActivityRef.current = Date.now(); // don't let frozen time spill into idle
      (async () => {
        try {
          await api.post("/shifts/break", { action: "end" });
          await api.post("/shifts/heartbeat", {
            idle_seconds: Math.round(idleSecondsRef.current),
            break_seconds: Math.round(breakSecondsRef.current),
            on_break: false,
          });
        } catch (e) {
          console.debug("[ShiftCounter] scheduled-break end failed:", e?.response?.status || e?.message);
        }
      })();
    }
  }, [freezeStatus, shift?.id]);

  // 2) Activity listeners — reset idle clock on input.
  //    Also handle `visibilitychange`: switching TO the tab counts as
  //    activity resumption. If the away-window exceeded the extra-break
  //    threshold (10 min), we open a modal asking the agent to log a
  //    reason. This matches the admin's 2026-02-07 spec.
  useEffect(() => {
    const handleReturn = () => {
      const now = Date.now();
      // Check if we're returning from a long away-window
      if (awayStartRef.current && !extraBreakPromptShown.current) {
        const awayMs = now - awayStartRef.current;
        if (awayMs >= EXTRA_BREAK_THRESHOLD_MS && !onBreakRef.current) {
          extraBreakPromptShown.current = true;
          setExtraBreakPrompt({ minutes: Math.round(awayMs / 60000) });
        }
      }
      // Reset trackers on activity
      lastActivityRef.current = now;
      awayStartRef.current = null;
    };
    const onActivity = () => handleReturn();
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        handleReturn();
      } else {
        // Tab just went hidden — start the away-window IF we don't
        // already have one running (mouse could have been idle for a
        // few minutes before the user tabbed away).
        if (!awayStartRef.current) awayStartRef.current = lastActivityRef.current;
      }
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // 3) 1-second tick — accumulate idle / break.
  //    Idle rule (hybrid): user is idle when either
  //      (a) tab is HIDDEN and >5 min since last activity, OR
  //      (b) tab is VISIBLE and >5 min since last activity.
  //    Break time only accumulates when explicitly on break.
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      const delta = (now - lastTickRef.current) / 1000;
      lastTickRef.current = now;
      if (onBreakRef.current) {
        breakSecondsRef.current += delta;
      } else {
        const awayMs = now - lastActivityRef.current;
        if (awayMs >= IDLE_THRESHOLD_MS) {
          idleSecondsRef.current += delta;
          // Track when this away-window began for the extra-break prompt.
          // We set it at the *moment* we crossed the 5-min line so the
          // reported "away X min" is honest, not inflated.
          if (!awayStartRef.current) {
            awayStartRef.current = lastActivityRef.current;
            extraBreakPromptShown.current = false;
          }
        }
      }
      setTick((t) => t + 1);
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // 4) Heartbeat — push current accumulators to the backend.
  //    Gated on `shift` so we don't spam /shifts/heartbeat with 404s
  //    when no active shift exists (e.g., agent ended shift). Backs off
  //    by clearing `shift` on a 404 so the interval auto-stops; the
  //    next /shifts/start (re-mount or "End Shift → new shift") will
  //    re-enable it.
  useEffect(() => {
    if (!shift?.id) return undefined;
    const id = setInterval(async () => {
      try {
        await api.post("/shifts/heartbeat", {
          idle_seconds: Math.round(idleSecondsRef.current),
          break_seconds: Math.round(breakSecondsRef.current),
          on_break: onBreakRef.current,
        });
      } catch (e) {
        // 404 = "No active shift" on the server (shift ended elsewhere).
        // Drop local shift to stop the polling loop until the user
        // explicitly starts a new shift. 401 is non-fatal (token refresh
        // in flight) — let the next tick retry.
        if (e?.response?.status === 404) {
          setShift(null);
        }
        console.debug("[ShiftCounter] heartbeat skipped:", e?.response?.status || e?.message);
      }
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [shift?.id]);

  // 5) Live refresh of leads_submitted on lead-submission events
  useEffect(() => {
    const onLeadSubmitted = async () => {
      try {
        const { data } = await api.get("/shifts/my");
        if (data) setShift((s) => (s ? { ...s, leads_submitted: data.leads_submitted } : data));
      } catch (e) {
        console.debug("[ShiftCounter] live refresh failed:", e?.response?.status || e?.message);
      }
    };
    window.addEventListener("lead-submitted", onLeadSubmitted);
    return () => window.removeEventListener("lead-submitted", onLeadSubmitted);
  }, []);

  // Take Break: pick a reason first. End Break: just send action=end.
  const startBreakWithReason = useCallback(async (reason) => {
    setReasonOpen(false);
    onBreakRef.current = true;
    setOnBreak(true);
    setBreakReason(reason);
    try {
      await api.post("/shifts/break", { action: "start", reason });
      await api.post("/shifts/heartbeat", {
        idle_seconds: Math.round(idleSecondsRef.current),
        break_seconds: Math.round(breakSecondsRef.current),
        on_break: true,
      });
    } catch (e) {
      console.debug("[ShiftCounter] start-break failed:", e?.response?.status || e?.message);
    }
  }, []);

  const endBreak = useCallback(async () => {
    onBreakRef.current = false;
    setOnBreak(false);
    setBreakReason(null);
    try {
      await api.post("/shifts/break", { action: "end" });
      await api.post("/shifts/heartbeat", {
        idle_seconds: Math.round(idleSecondsRef.current),
        break_seconds: Math.round(breakSecondsRef.current),
        on_break: false,
      });
    } catch (e) {
      console.debug("[ShiftCounter] end-break failed:", e?.response?.status || e?.message);
    }
  }, []);

  const endShift = useCallback(async () => {
    if (!window.confirm("End your shift? The counter will stop and reset on next sign-in. Use this only when leaving for the day.")) return;
    try {
      // Flush the latest counters first so they are persisted on the closed shift.
      await api.post("/shifts/heartbeat", {
        idle_seconds: Math.round(idleSecondsRef.current),
        break_seconds: Math.round(breakSecondsRef.current),
        on_break: onBreakRef.current,
      });
      await api.post("/shifts/end");
      // Reset local state so the bar shows a fresh shift if the user signs in again.
      setShift(null);
      idleSecondsRef.current = 0;
      breakSecondsRef.current = 0;
      onBreakRef.current = false;
      setOnBreak(false);
      setBreakReason(null);
    } catch (e) {
      console.warn("[ShiftCounter] end-shift failed:", e?.response?.status || e?.message);
    }
  }, []);

  // Submit extra-break reason to backend, then close the prompt.
  const submitExtraBreak = useCallback(async (reason) => {
    const mins = extraBreakPrompt?.minutes || 0;
    setExtraBreakPrompt(null);
    try {
      await api.post("/shifts/extra-break", { minutes: mins, reason });
    } catch (e) {
      // Non-fatal — reason still visible in shift heartbeat via idle,
      // just missing the explicit label. Log for debug.
      console.debug("[ShiftCounter] extra-break log failed:", e?.response?.status || e?.message);
    }
  }, [extraBreakPrompt]);

  // Derived counters
  let shiftSeconds = 0;
  if (shift?.start_at) {
    shiftSeconds = (Date.now() - new Date(shift.start_at).getTime()) / 1000;
  }
  const idleSec = idleSecondsRef.current;
  const breakSec = breakSecondsRef.current;
  const activeSec = Math.max(0, shiftSeconds - idleSec - breakSec);

  return (
    <div
      data-testid="shift-counter"
      className={`flex items-center gap-2 flex-wrap ${compact ? "" : "border border-border bg-background rounded-sm px-3 py-2"}`}
    >
      <Stat icon={Clock} label="Shift" value={fmt(shiftSeconds)} testid="shift-time" />
      <Stat icon={CheckCircle2} label="Active" value={fmt(activeSec)} tone="emerald" testid="shift-active" />
      <Stat icon={MoonStar} label="Idle" value={fmt(idleSec)} tone="amber" testid="shift-idle" />
      <Stat icon={Coffee} label="Break" value={fmt(breakSec)} tone="rose" testid="shift-break" />
      {onBreak && breakReason && (
        <span className="text-xs px-2 py-1 rounded-sm bg-rose-50 text-rose-700 border border-rose-200" data-testid="shift-break-reason">
          {breakReason}
        </span>
      )}
      {shift && (
        <Stat icon={Timer} label="Submitted" value={shift.leads_submitted ?? 0} testid="shift-leads-submitted" />
      )}

      {/* Break control: when ON break => End Break. Otherwise => open reason menu. */}
      <div className="relative ml-1">
        {onBreak ? (
          <Button
            type="button" size="sm" variant="default"
            onClick={endBreak}
            data-testid="shift-end-break"
            className="rounded-sm h-8 bg-rose-600 hover:bg-rose-700 text-white"
          >
            <Play size={14} className="mr-1" /> End Break
          </Button>
        ) : (
          <Button
            type="button" size="sm" variant="outline"
            onClick={() => setReasonOpen((v) => !v)}
            data-testid="shift-break-toggle"
            className="rounded-sm h-8"
          >
            <Coffee size={14} className="mr-1" /> Take Break
          </Button>
        )}
        {reasonOpen && !onBreak && (
          <div
            data-testid="break-reason-menu"
            className="absolute right-0 mt-1 z-50 min-w-[180px] bg-background border border-border rounded-sm shadow-md py-1"
          >
            <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground">Reason for break</div>
            {BREAK_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => startBreakWithReason(r)}
                data-testid={`break-reason-${r.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-secondary/60"
              >
                {r}
              </button>
            ))}
          </div>
        )}
      </div>

      {shift && (
        <Button
          type="button" size="sm" variant="outline"
          onClick={endShift}
          data-testid="shift-end"
          className="rounded-sm h-8 text-muted-foreground hover:text-rose-700 hover:border-rose-300"
          title="End shift (when leaving for the day)"
        >
          <LogOut size={13} className="mr-1" /> End Shift
        </Button>
      )}

      {/* Extra-break prompt — surfaces after 10+ min of no activity.
          Backdrop is fixed to viewport so it can't be dismissed by
          clicking elsewhere (agent must pick a reason). Six choices
          match the admin spec; "Other" opens a free-text box. */}
      {extraBreakPrompt && (
        <div
          className="fixed inset-0 z-[200] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4"
          data-testid="extra-break-modal"
        >
          <div className="bg-background border border-border rounded-md shadow-xl max-w-sm w-full p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center flex-shrink-0">
                <MoonStar size={20} />
              </div>
              <div>
                <div className="font-bold text-sm">You were away {extraBreakPrompt.minutes} min</div>
                <div className="text-xs text-muted-foreground mt-0.5">Unplanned absence — please tell us why so admin can audit fairly.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-1.5" data-testid="extra-break-reasons">
              {EXTRA_BREAK_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => submitExtraBreak(r)}
                  data-testid={`extra-break-reason-${r.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className="px-3 py-2 text-xs border border-border rounded-sm hover:bg-secondary/60 hover:border-primary/40 transition-colors"
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testid }) {
  const toneClass =
    tone === "emerald" ? "text-emerald-700" :
    tone === "amber"   ? "text-amber-700" :
    tone === "rose"    ? "text-rose-700"   : "text-foreground";
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 border border-border rounded-sm bg-secondary/30" data-testid={testid}>
      <Icon size={13} className={toneClass} />
      <div className="flex items-baseline gap-1.5 leading-none">
        <span className="meta-label text-[9px]">{label}</span>
        <span className={`font-mono text-xs ${toneClass}`}>{value}</span>
      </div>
    </div>
  );
}
